
import { Vector2D, SimulationMode, ShapeType, Ball, EscapeGapAlignment, EscapeRotationMode, RhythmicSyncMode, MorphData, EscapeRing, PlaylistItem, DrawerColorMode, DrawerBrushType, DrawerSymmetryMode } from './base';
import type { SimulationConfig } from './config';

export type SpeedIncreaseMode = 'STATIC' | 'TIME' | 'COLLISION';
export type BallGrowthMode = 'NONE' | 'TIME' | 'COLLISION' | 'PLAYLIST_SYNC';
export type ContainerShrinkMode = 'NONE' | 'TIME' | 'COLLISION';
export type EscapeColorMode = 'RAINBOW' | 'ALTERNATING' | 'GRADIENT' | 'UNIFORM' | 'INDIVIDUAL';
export type RingCounterPosition = 'CENTER' | 'TOP_CENTER' | 'BOTTOM_CENTER' | 'FOLLOW_BALL' | 'NONE';

export type EscapeBreakAnimation = 
  'SHATTER' | 'FADE' | 'EXPLODE' | 'PARTICLES' | 'EXPLOSION' | 'RING_EXPAND' | 'GLOW_FADE' | 'STARDUST' | 'VORTEX' | 
  'PIXEL_BURST' | 'SONIC_BOOM' | 'FIREWORK' | 'GHOST_ECHO' | 'SHARD_STORM' | 'NEBULA' | 'DATA_STREAM' | 'RIPPLE_WAVE' | 
  'SHOCKWAVE' | 'GLITCH_SQUARE' | 'CONFETTI' | 'ENERGY_ORBS' | 'FRACTAL_GROWTH' | 'RAINBOW_BURST' | 'HEXAGON_GRID' | 
  'PLASMA_WAVE' | 'SMOKE_RING' | 'LIGHTNING_STRIKE' | 'SPIRAL_STARS' | 'CRYSTAL_GROWTH' | 'GALAXY_SWIRL' | 'PHANTOM_RINGS' | 
  'METEOR_SHOWER' | 'QUANTUM_LEAP' | 'FIRE_SPIRAL' | 'AURORA_BOREALIS' | 'DEEP_SEA_BUBBLES' | 'TIME_WARP' | 'PHYSICS_SHATTER' | 
  'GRAVITY_CRUMBLE' | 'EXPLOSIVE_SHARDS' | 'FLOATING_DEBRIS' | 'VORTEX_SHATTER' | 'MOLTEN_DRIP' | 'BOUNCY_CHUNKS' | 
  'PIXEL_GRAVITY' | 'RADIAL_SLICE' | 'IMPLODE_EXPLODE' | 'CHAIN_REACTION' | 'SHRAPNEL' | 'DEBRIS_CLOUD' | 'ASTEROID_FIELD' | 
  'VOLCANIC_BURST' | 'SUPERNOVA_FRAG' | 'BLACK_HOLE_EAT' | 'NEBULA_DRIFT' | 'COMET_TAIL' | 'PRISM_SPLIT' | 'GRAVITY_DUST';

export type CollisionEffectType = 
  'WAVE' | 'PARTICLES' | 'FLASH' | 'NONE' | 'RIPPLE' | 'GRAVITY_PARTICLES' | 'ORBITAL_SPIN' | 'ELASTIC_RING' | 'LIQUID_SPLASH' | 
  'SPARKS' | 'SHATTER_GLASS' | 'PIXEL_CRUMBLE' | 'NEON_PULSE' | 'VORTEX_SUCK' | 'FIREWORKS' | 'SMOKE_PUFF' | 'GLITCH_FRAGMENTS' | 
  'LIGHTNING_BOLT' | 'BUBBLE_POP' | 'SPIRAL_GALAXY' | 'SONIC_WAVE' | 'QUANTUM_TUNNEL' | 'DNA_HELIX' | 'MATRIX_RAIN' | 
  'PLASMA_BLOB' | 'CRYSTAL_SHARDS' | 'GHOST_SOULS' | 'CYBER_HEX' | 'INK_SPLAT' | 'SUPERNOVA' | 'CONFETTI_CANNON';

export enum ParallaxEffectType {
  STARRY_NIGHT = 'STARRY_NIGHT',
  NEBULA_DRIFT = 'NEBULA_DRIFT',
  GRID_WORLD = 'GRID_WORLD',
  FLOATING_SHAPES = 'FLOATING_SHAPES',
  DATA_STREAM = 'DATA_STREAM',
  GEOMETRIC_ABSTRACTION = 'GEOMETRIC_ABSTRACTION',
  VORTEX_TUNNEL = 'VORTEX_TUNNEL',
  PARTICLE_FIELD = 'PARTICLE_FIELD',
  AURORA_WAVES = 'AURORA_WAVES',
  CIRCUIT_BOARD = 'CIRCUIT_BOARD',
  RETRO_GRID = 'RETRO_GRID',
  MATRIX_RAIN = 'MATRIX_RAIN',
  FRACTAL_CLOUDS = 'FRACTAL_CLOUDS',
  CYBER_PUNK_CITY = 'CYBER_PUNK_CITY',
  OCEAN_WAVES = 'OCEAN_WAVES',
  DNA_HELIX = 'DNA_HELIX',
  WATERMARK = 'WATERMARK',
  CONSTELLATIONS = 'CONSTELLATIONS',
  DIGITAL_RAIN = 'DIGITAL_RAIN',
  NEON_LINES = 'NEON_LINES',
  BUBBLE_FIELD = 'BUBBLE_FIELD',
  HEXAGON_GRID = 'HEXAGON_GRID',
  SPIRAL_GALAXY = 'SPIRAL_GALAXY',
  ABSTRACT_WAVES = 'ABSTRACT_WAVES',
  TECH_PIXELS = 'TECH_PIXELS',
  
  // Phase 2: Nature & Organic
  LAVA_FLOW = 'LAVA_FLOW',
  MOSS_GROWTH = 'MOSS_GROWTH',
  CLOUD_FORMATIONS = 'CLOUD_FORMATIONS',
  ROOT_SYSTEMS = 'ROOT_SYSTEMS',
  RIPPLE_POOLS = 'RIPPLE_POOLS',
  RAIN_STREAKS = 'RAIN_STREAKS',
  FALLING_LEAVES = 'FALLING_LEAVES',
  SNOW_FLURRY = 'SNOW_FLURRY',
  SWAYING_GRASS = 'SWAYING_GRASS',
  SAND_DUNES = 'SAND_DUNES',
  CORAL_REEF = 'CORAL_REEF',
  BAMBOO_FOREST = 'BAMBOO_FOREST',
  THUNDER_STORM = 'THUNDER_STORM',
  FIRE_EMBERS = 'FIRE_EMBERS',
  JUNGLE_VINES = 'JUNGLE_VINES',
  CHERRY_BLOSSOM = 'CHERRY_BLOSSOM',
  ICE_CRYSTALS = 'ICE_CRYSTALS',
  RIVER_CURRENT = 'RIVER_CURRENT',
  VOLCANIC_ASH = 'VOLCANIC_ASH',
  MIST_DRIFT = 'MIST_DRIFT',
  BIO_LUMINESCENCE = 'BIO_LUMINESCENCE',
  PETAL_STORM = 'PETAL_STORM',
  SUN_BEAMS = 'SUN_BEAMS',
  UNDERWATER_LIGHT = 'UNDERWATER_LIGHT',
  SPORE_DRIFT = 'SPORE_DRIFT',
  WIND_WHIRLS = 'WIND_WHIRLS',
  
  // Phase 3: Abstract & Geometric
  VORONOI_CELLS = 'VORONOI_CELLS',
  MOIRE_PATTERNS = 'MOIRE_PATTERNS',
  LISSAJOUS_CURVES = 'LISSAJOUS_CURVES',
  FLOWER_OF_LIFE = 'FLOWER_OF_LIFE',
  KALEIDOSCOPE_MIRROR = 'KALEIDOSCOPE_MIRROR',
  FRACTAL_MANDALAS = 'FRACTAL_MANDALAS',
  SACRED_GEOMETRY = 'SACRED_GEOMETRY',
  METATRON_CUBE = 'METATRON_CUBE',
  MANDELBROT_SET = 'MANDELBROT_SET',
  JULIA_SET = 'JULIA_SET',
  PENROSE_TILING = 'PENROSE_TILING',
  SIERPINSKI_TRIANGLE = 'SIERPINSKI_TRIANGLE',
  KOCH_SNOWFLAKE = 'KOCH_SNOWFLAKE',
  HARMONIC_WAVES = 'HARMONIC_WAVES',
  ISOMETRIC_CUBES = 'ISOMETRIC_CUBES',
  DOT_MATRIX = 'DOT_MATRIX',
  WIREFRAME_TERRAIN = 'WIREFRAME_TERRAIN',
  POLYGON_SPIRALS = 'POLYGON_SPIRALS',
  TESSELLATED_HEXAGONS = 'TESSELLATED_HEXAGONS',
  MINIMALIST_LINES = 'MINIMALIST_LINES',
  BRUTALIST_BLOCKS = 'BRUTALIST_BLOCKS',
  OP_ART_ILLUSION = 'OP_ART_ILLUSION',
  SYMMETRIC_ORBS = 'SYMMETRIC_ORBS',
  HYPNOTIC_SPIRALS = 'HYPNOTIC_SPIRALS',
  TRIANGULAR_SUBDIVISION = 'TRIANGULAR_SUBDIVISION',
  CRYSTALLINE_LATTICE = 'CRYSTALLINE_LATTICE',
  FLUID_SUBSTANCE = 'FLUID_SUBSTANCE',
  VECTOR_FIELD = 'VECTOR_FIELD',

  // Phase 4: Cyber & Tactical
  HUD_OVERLAY = 'HUD_OVERLAY',
  TERMINAL_STREAMS = 'TERMINAL_STREAMS',
  DATA_NODES = 'DATA_NODES',
  RADAR_SWEEP = 'RADAR_SWEEP',
  SCHEMATIC_GRID = 'SCHEMATIC_GRID',
  GLITCH_PIXELS = 'GLITCH_PIXELS',
  SCANLINE_PROCESSOR = 'SCANLINE_PROCESSOR',
  BINARY_CASCADE = 'BINARY_CASCADE',
  CIRCUIT_FLOW = 'CIRCUIT_FLOW',
  NEURAL_NETWORK = 'NEURAL_NETWORK',
  FIRMWARE_DIAGRAM = 'FIRMWARE_DIAGRAM',
  TARGETING_RETICLES = 'TARGETING_RETICLES',
  SATELLITE_LINK = 'SATELLITE_LINK',
  SECURITY_MESH = 'SECURITY_MESH',
  ENCRYPTION_MATRIX = 'ENCRYPTION_MATRIX',
  TECH_SPECS = 'TECH_SPECS',
  ANALOG_NOISE = 'ANALOG_NOISE',
  VIRTUAL_REALITY = 'VIRTUAL_REALITY',
  CYBER_SPACE = 'CYBER_SPACE',
  DIGITAL_SIGNAL = 'DIGITAL_SIGNAL',
  PULSE_WAVE = 'PULSE_WAVE',
  SIGNAL_INTERFERENCE = 'SIGNAL_INTERFERENCE',
  BIO_HAZARD = 'BIO_HAZARD',
  RADIATION_SCAN = 'RADIATION_SCAN',
  SONAR_PING = 'SONAR_PING',
  COORDINATE_SYSTEM = 'COORDINATE_SYSTEM',
  DEBUG_CONSOLE = 'DEBUG_CONSOLE',
  SYSTEM_MAINFRAME = 'SYSTEM_MAINFRAME',

  // Phase 5: Cosmic & Dimensional
  SUPERNOVA = 'SUPERNOVA',
  EVENT_HORIZON = 'EVENT_HORIZON',
  DARK_MATTER = 'DARK_MATTER',
  STAR_CLUSTERS = 'STAR_CLUSTERS',
  WORMHOLE = 'WORMHOLE',
  COSMIC_BACKGROUND = 'COSMIC_BACKGROUND',
  QUANTUM_FOAM = 'QUANTUM_FOAM',
  SOLAR_FLARE = 'SOLAR_FLARE',
  PULSAR_BEAM = 'PULSAR_BEAM',
  NEBULA_GLOW = 'NEBULA_GLOW',
  BLACK_HOLE_SINGULARITY = 'BLACK_HOLE_SINGULARITY',
  QUASAR_JET = 'QUASAR_JET',
  GALACTIC_CORE = 'GALACTIC_CORE',
  STELLAR_NURSERY = 'STELLAR_NURSERY',
  COMET_TAIL = 'COMET_TAIL',
  METEOR_SHOWER = 'METEOR_SHOWER',
  ATMOSPHERIC_ENTRY = 'ATMOSPHERIC_ENTRY',
  DIMENSIONAL_RIFT = 'DIMENSIONAL_RIFT',
  STRINGS_THEORY = 'STRINGS_THEORY',
  MULTIVERSE_BUBBLES = 'MULTIVERSE_BUBBLES',
  ENTROPY_HEAT_DEATH = 'ENTROPY_HEAT_DEATH',
  GRAVITY_WAVES = 'GRAVITY_WAVES',
  SPACE_TIME_DISTORTION = 'SPACE_TIME_DISTORTION',
  AURORA_BOREALIS = 'AURORA_BOREALIS',
  ANTI_MATTER = 'ANTI_MATTER',
  VOID_VOID = 'VOID_VOID',
  CELESTIAL_BODY = 'CELESTIAL_BODY',
  EXPANDING_UNIVERSE = 'EXPANDING_UNIVERSE',

  // Phase 6: Artistic & Textures
  OIL_PAINTING = 'OIL_PAINTING',
  SKETCH_BOOK = 'SKETCH_BOOK',
  STAINED_GLASS = 'STAINED_GLASS',
  MOSAIC_TILES = 'MOSAIC_TILES',
  CARBON_FIBER = 'CARBON_FIBER',
  BRUSHED_METAL = 'BRUSHED_METAL',
  LIQUID_METAL = 'LIQUID_METAL',
  INK_BLEED = 'INK_BLEED',
  WATERCOLOR = 'WATERCOLOR',
  POP_ART = 'POP_ART',
  COMIC_DOTS = 'COMIC_DOTS',
  OLD_FILM = 'OLD_FILM',
  VHS_GLITCH = 'VHS_GLITCH',
  PRISM_LIGHT = 'PRISM_LIGHT',
  CHALK_DRAWING = 'CHALK_DRAWING',
  PIXEL_ART_LANDSCAPE = 'PIXEL_ART_LANDSCAPE',
  SYNTHWAVE_GRID = 'SYNTHWAVE_GRID',
  VAPOR_WAVE = 'VAPOR_WAVE',
  CHARCOAL_SKETCH = 'CHARCOAL_SKETCH',
  CROSS_HATCH = 'CROSS_HATCH',
  WOOD_GRAIN = 'WOOD_GRAIN',
  MARBLE_TEXTURE = 'MARBLE_TEXTURE',

  // Phase 7: Media & External
  IMAGE_OVERLAY = 'IMAGE_OVERLAY',
  SCROLLING_IMAGE = 'SCROLLING_IMAGE',
  VIDEO_BACKDROP = 'VIDEO_BACKDROP',
  VIDEO_TEXTURE = 'VIDEO_TEXTURE',
  MEDIA_MOSAIC = 'MEDIA_MOSAIC',

  NONE = 'NONE'
}

export enum BaseBackgroundEffectType {
  SOLID = 'SOLID',
  LINEAR_GRADIENT = 'LINEAR_GRADIENT',
  RADIAL_GRADIENT = 'RADIAL_GRADIENT',
  MESH_GRADIENT = 'MESH_GRADIENT',
  PLASMA = 'PLASMA',
  STARFIELD = 'STARFIELD',
  SCANLINE = 'SCANLINE',
  NOISE_GRAIN = 'NOISE_GRAIN',
  GEOMETRIC_TILES = 'GEOMETRIC_TILES',
  AURORA_BASE = 'AURORA_BASE',
  DEEP_SPACE = 'DEEP_SPACE',
  MATRIX_CODE = 'MATRIX_CODE',
  RGB_WAVE = 'RGB_WAVE',
  CYBER_GRID = 'CYBER_GRID',
  KALEIDOSCOPE = 'KALEIDOSCOPE',
  VIGNETTE = 'VIGNETTE',
  STATIC_IMAGE = 'STATIC_IMAGE',
  IMAGE_BACKDROP = 'IMAGE_BACKDROP',
  VIDEO_STREAM = 'VIDEO_STREAM'
}

export interface SpaceSpiralRoundConfig {
  id: string;
  name: string;
  duration: number;
  forwardThrust: number;
  thrustDuration: number;
  thrustAngle: number;
  gravityPower: number;
  airResistance: number;
  restitution: number;
  boxRestitution: number;
}

export interface BallPlacementConfig {
  id: string;
  mode: 'CENTER' | 'MANUAL' | 'BORDER' | 'ANGLE';
  posX: number;
  posY: number;
  border: 'TOP' | 'BOTTOM' | 'LEFT' | 'RIGHT';
  placementAngle: number;
  distance: number;
  directionAngle: number;
  randomDirection: boolean;
  speed: number;
  color?: string;
}

export interface AppSave {
  id: string;
  name: string;
  config: SimulationConfig;
  isDefault: boolean;
  updatedAt: number;
}

export interface FXPreset {
  name: string;
  settings: Partial<SimulationConfig>;
}

export interface SongSlice {
  start: number;
  duration: number;
}

export interface TrailCircle {
  pos: Vector2D;
  radius: number;
  hue: number;
  alpha?: number;
}

export interface CollisionEvent {
  id: string;
  pos: Vector2D;
  startTime: number;
  radius: number;
  color: string;
  effectType: string;
  duration: number;
}

export interface BlastEvent {
  id: string;
  pos: Vector2D;
  startTime: number;
  duration: number;
  maxRadius: number;
  color: string;
  animationType: string;
  intensity: number;
  shape?: ShapeType;
  rotation?: number;
  gapAngle?: number;
  morphData?: {
      from: ShapeType;
      to: ShapeType;
      factor: number;
      baseRadius?: number;
  };
}

export interface BallPiece {
  id: string;
  pos: Vector2D;
  vel: Vector2D;
  radius: number;
  color: string;
  startTime: number;
  duration: number;
  angle: number;
  spin: number;
  shape: ShapeType;
}

export interface RhythmicSimulation {
  id: string;
  name: string;
  trackIndices: number[];
  backgroundTrackIndices?: number[];
  mutedTrackIndices?: number[];
  settings?: Partial<SimulationConfig>;
}

export interface RhythmicSimulationState {
  id: string;
  ball: Ball | null;
  containerRadius: number;
  rhythmicIndex: number;
  ballGrowthRate: number;
  rhythmicTimeScale: number;
  rhythmicTimeScaleRate: number;
  rhythmicTimeScaleAcceleration: number;
  center: Vector2D;
  targetCenter: Vector2D;
  active: boolean;
  spawnTime: number;
  trackIndices: number[];
  backgroundTrackIndices: number[];
  mutedTrackIndices: number[];
  settings?: Partial<SimulationConfig>;
}

export interface VisualPreset {
  name: string;
  category: string;
  settings: Partial<SimulationConfig>;
}
